// Achievement catalogue + progression logic. Achievements are grouped into FAMILIES (one badge art each);
// every family has ascending tiers. Progression is read purely from the cross-game Profile, so nothing here
// mutates state. The UI shows completed tiers in one tab and only the NEXT uncompleted tier of each family in
// the other (progressive reveal) - you never see level 5 before you clear level 1.

import type { Profile } from '../platform/storage';

// Effort class of a family, which sets its elmas payout. Rewards are flat per family (every tier of an
// easy family pays 10, etc.); because each tier is a separate one-time achievement, clearing a whole
// family still pays out cumulatively, which naturally rewards the grind. Tunable data, not balance-critical.
export type AchTier = 'easy' | 'medium' | 'hard';
export const ACH_TIER_ELMAS: Record<AchTier, number> = { easy: 10, medium: 25, hard: 50 };

export interface AchFamily {
  key: string; // stable id; also the art file: public/art/achievements/<key>.png
  name: string; // base title; a tier numeral is appended per tier
  desc: string; // one-line, with a {n} placeholder for the tier threshold
  emoji: string; // fallback icon when the art image is missing
  tier: AchTier; // effort class -> elmas reward
  tiers: number[]; // ascending thresholds
  value: (p: Profile) => number; // current progress toward this family from the profile
}

const WIN_TIERS = [1, 3, 5, 10];
const KILL_TIERS = [10, 40, 100];
const BUILD_TIERS = [5, 20, 60];

// Tutorial chapter ids, mirrored from src/ui/tutorialChapters.ts (CHAPTER_IDS). Duplicated as literals on
// purpose: this file reads purely from the Profile and must not import UI code (circular import). The
// filter stops a hand-edited profile array from over-counting toward the badge.
const TUTORIAL_CHAPTERS = ['basics', 'economy', 'combat', 'navy', 'council'];

export const FAMILIES: AchFamily[] = [
  // --- onboarding: one-shot milestones that pay a starter chunk of elmas. 'tutorial' keeps its key (so
  // the unlocked id 'tutorial#0' and the badge art path stay stable) but now counts completed CHAPTERS:
  // tiers [5] keeps it ONE badge while the count gives the achievements page partial progress for free.
  { key: 'tutorial', name: 'First Lesson', desc: 'Learn the ropes: play 5 guided trail levels', emoji: '🎓', tier: 'easy', tiers: [5], value: (p) => Math.min(5, Math.max((p.tutorialChapters ?? []).filter((id) => TUTORIAL_CHAPTERS.includes(id)).length, (p.campaignGuided ?? []).length)) },
  { key: 'first_match', name: 'Baptism by Battle', desc: 'Finish your first real match', emoji: '⚔️', tier: 'easy', tiers: [1], value: (p) => p.games },
  // --- collector: buy cosmetics from the shop ---
  { key: 'shop', name: 'Big Spender', desc: 'Buy {n} items from the shop', emoji: '🛒', tier: 'medium', tiers: [1, 5, 20], value: (p) => p.itemsBought },
  // --- race mastery: win with each race ---
  { key: 'win_orc', name: 'Grimskar Warlord', desc: 'Win {n} games as the Orcs', emoji: '👹', tier: 'medium', tiers: WIN_TIERS, value: (p) => p.byRace['orc']?.wins ?? 0 },
  { key: 'win_elf', name: 'Silvergrove Warden', desc: 'Win {n} games as the Elves', emoji: '🏹', tier: 'medium', tiers: WIN_TIERS, value: (p) => p.byRace['elf']?.wins ?? 0 },
  { key: 'win_dwarf', name: 'Stonehearth Thane', desc: 'Win {n} games as the Dwarves', emoji: '⛏️', tier: 'medium', tiers: WIN_TIERS, value: (p) => p.byRace['dwarf']?.wins ?? 0 },
  { key: 'win_human', name: 'Highmarch Champion', desc: 'Win {n} games as the Humans', emoji: '⚔️', tier: 'medium', tiers: WIN_TIERS, value: (p) => p.byRace['human']?.wins ?? 0 },
  { key: 'win_undead', name: 'Mordhollow Lich', desc: 'Win {n} games as the Undead', emoji: '💀', tier: 'medium', tiers: WIN_TIERS, value: (p) => p.byRace['undead']?.wins ?? 0 },
  // --- conquest: total tiles captured across all games ---
  { key: 'tiles', name: 'Land Grabber', desc: 'Capture {n} tiles across all your games', emoji: '🚩', tier: 'easy', tiers: [15, 30, 50, 75, 100], value: (p) => p.tilesCaptured },
  // --- slayer: enemy units killed by type ---
  { key: 'kill_warrior', name: 'Blade Breaker', desc: 'Slay {n} enemy Warriors', emoji: '⚔️', tier: 'medium', tiers: KILL_TIERS, value: (p) => p.kills[0] ?? 0 },
  { key: 'kill_archer', name: 'Bow Snapper', desc: 'Slay {n} enemy Archers', emoji: '🏹', tier: 'medium', tiers: KILL_TIERS, value: (p) => p.kills[1] ?? 0 },
  { key: 'kill_rider', name: 'Saddle Buster', desc: 'Slay {n} enemy Riders', emoji: '🐎', tier: 'medium', tiers: KILL_TIERS, value: (p) => p.kills[2] ?? 0 },
  { key: 'kill_spearman', name: 'Pike Cracker', desc: 'Slay {n} enemy Spearmen', emoji: '🔱', tier: 'medium', tiers: KILL_TIERS, value: (p) => p.kills[3] ?? 0 },
  // --- quartermaster: units trained by type ---
  { key: 'build_warrior', name: 'Blade Forged', desc: 'Train {n} Warriors', emoji: '⚒️', tier: 'easy', tiers: BUILD_TIERS, value: (p) => p.built[0] ?? 0 },
  { key: 'build_archer', name: "Fletcher's Mark", desc: 'Train {n} Archers', emoji: '🎯', tier: 'easy', tiers: BUILD_TIERS, value: (p) => p.built[1] ?? 0 },
  { key: 'build_rider', name: 'Saddle Muster', desc: 'Train {n} Riders', emoji: '🐴', tier: 'easy', tiers: BUILD_TIERS, value: (p) => p.built[2] ?? 0 },
  { key: 'build_spearman', name: 'Phalanx Roll', desc: 'Train {n} Spearmen', emoji: '🛡️', tier: 'easy', tiers: BUILD_TIERS, value: (p) => p.built[3] ?? 0 },
  // --- career: total wins + games played ---
  { key: 'wins', name: 'Laurels of Victory', desc: 'Win {n} games with any race', emoji: '🏆', tier: 'hard', tiers: WIN_TIERS, value: (p) => p.wins },
  { key: 'games', name: 'Seasoned Campaigner', desc: 'Play {n} games, win or lose', emoji: '🎖️', tier: 'easy', tiers: [3, 10, 25, 50], value: (p) => p.games },

  // --- online multiplayer (tallied only for real matches: 2+ human seats, past the opening rounds) ---
  { key: 'mp_games', name: 'Far Isles Campaigner', desc: 'Play {n} online matches', emoji: '🌐', tier: 'easy', tiers: [1, 5, 15, 40], value: (p) => p.mpGames },
  { key: 'mp_wins', name: 'Conqueror of Rivals', desc: 'Win {n} online matches', emoji: '👑', tier: 'hard', tiers: [1, 5, 15], value: (p) => p.mpWins },
  { key: 'mp_kills', name: 'Bane of Warlords', desc: 'Slay {n} enemy units in online matches', emoji: '🗡️', tier: 'medium', tiers: [10, 50, 150], value: (p) => p.mpKills },
];

const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI'];

export interface Achievement {
  id: string; // `${familyKey}#${tierIndex}` - stable across saves
  familyKey: string; // the badge art key
  name: string; // family name + tier numeral
  desc: string; // desc with {n} filled in
  emoji: string;
  tierIndex: number;
  threshold: number;
  tier: AchTier; // effort class inherited from the family
  reward: number; // elmas paid out once when this achievement unlocks
}

/** Flat list of every achievement (family × tier), in catalogue order. */
export const ACHIEVEMENTS: Achievement[] = FAMILIES.flatMap((f) =>
  f.tiers.map((thr, i) => ({
    id: `${f.key}#${i}`,
    familyKey: f.key,
    name: f.tiers.length > 1 ? `${f.name} ${ROMAN[i] ?? i + 1}` : f.name,
    // tier-1 grammar: every plural noun a {n}=1 can precede gets its singular form
    desc: f.desc.replace('{n}', String(thr)).replace(/\b1 games\b/, '1 game').replace(/\b1 online matches\b/, '1 online match').replace(/\b1 items\b/, '1 item'),
    emoji: f.emoji,
    tierIndex: i,
    threshold: thr,
    tier: f.tier,
    reward: ACH_TIER_ELMAS[f.tier],
  })),
);

/** Elmas paid out when this achievement unlocks (single choke point: showGameOver + tutorial finish). */
export const achReward = (a: Achievement): number => a.reward;

const familyOf = (key: string): AchFamily => FAMILIES.find((f) => f.key === key)!;

export const achValue = (p: Profile, a: Achievement): number => familyOf(a.familyKey).value(p);
export const isUnlocked = (p: Profile, a: Achievement): boolean => achValue(p, a) >= a.threshold;

/** All currently-unlocked achievement ids (for diffing before/after a game). */
export function unlockedIds(p: Profile): Set<string> {
  const s = new Set<string>();
  for (const a of ACHIEVEMENTS) if (isUnlocked(p, a)) s.add(a.id);
  return s;
}

/** Achievements that crossed their threshold between two profiles, the ones to celebrate after a game. */
export function newlyUnlocked(before: Profile, after: Profile): Achievement[] {
  const had = unlockedIds(before);
  return ACHIEVEMENTS.filter((a) => !had.has(a.id) && isUnlocked(after, a));
}

/** Completed achievements for the "Unlocked" tab: only the HIGHEST cleared tier of each family. A level III
 *  badge already implies I and II are done, so we never show repeated rows for the same family. */
export function completed(p: Profile): Achievement[] {
  const out: Achievement[] = [];
  for (const f of FAMILIES) {
    const v = f.value(p);
    let top = -1;
    for (let i = 0; i < f.tiers.length; i++) if (v >= (f.tiers[i] ?? Infinity)) top = i;
    if (top < 0) continue; // no tier cleared yet in this family
    const a = ACHIEVEMENTS.find((x) => x.familyKey === f.key && x.tierIndex === top);
    if (a) out.push(a);
  }
  return out;
}

/** The locked achievements to display: only the NEXT uncompleted tier of each family (progressive reveal),
 *  paired with the player's current progress toward it. */
export function nextLocked(p: Profile): { ach: Achievement; value: number }[] {
  const out: { ach: Achievement; value: number }[] = [];
  for (const f of FAMILIES) {
    const v = f.value(p);
    const next = f.tiers.findIndex((thr) => v < thr);
    if (next < 0) continue; // whole family maxed out
    const a = ACHIEVEMENTS.find((x) => x.familyKey === f.key && x.tierIndex === next)!;
    out.push({ ach: a, value: v });
  }
  return out;
}
